import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import { allCategories, searchArticles } from "@/lib/articles";
import { applyPageSeo } from "@/lib/seo";

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const [keyword, setKeyword] = useState(query);

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  useEffect(() => {
    applyPageSeo(
      query ? `“${query}”的搜索结果 - 鲁啊鲁` : "全站搜索 - 鲁啊鲁",
      `在鲁啊鲁中搜索“${query}”，查看文化、娱乐、生活等多领域的相关资讯内容。`,
      `鲁啊鲁搜索,${query},资讯检索`
    );
  }, [query]);

  const results = useMemo(() => (query ? searchArticles(query) : []), [query]);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) return;
    setSearchParams({ q: value });
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1 w-full">
        <div className="w-full px-4 md:px-8 pt-6">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "搜索结果" },
            ]}
          />
        </div>

        <div className="w-full px-4 md:px-8 mt-5">
          <div className="rounded-lg bg-background-100 p-5 md:p-7">
            <h1 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              全站搜索
            </h1>
            <form onSubmit={submit} className="mt-4 flex flex-col gap-3 sm:flex-row">
              <div className="flex flex-1 items-center gap-2 rounded-full border border-background-300 bg-background-50 px-4 py-2.5 focus-within:border-primary-400 transition-colors">
                <i className="ri-search-line w-5 h-5 flex items-center justify-center text-foreground-500"></i>
                <input
                  type="text"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="输入关键词，搜索你感兴趣的内容"
                  className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
                  aria-label="搜索关键词"
                />
              </div>
              <button
                type="submit"
                className="rounded-full bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                搜索
              </button>
            </form>
          </div>
        </div>

        <div className="w-full px-4 md:px-8 mt-6 pb-4">
          {query && (
            <p className="mb-4 text-sm text-foreground-600">
              关于
              <span className="mx-1 font-medium text-primary-600">“{query}”</span>
              共找到
              <span className="mx-1 font-medium text-foreground-900">{results.length}</span>
              条结果
            </p>
          )}

          {results.length > 0 ? (
            <div className="flex flex-col gap-4">
              {results.map((article) => (
                <ArticleCard key={article.id} article={article} variant="list" />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-4 py-20 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-400">
                <i className="ri-search-eye-line text-3xl"></i>
              </span>
              <h3 className="mt-5 font-heading text-lg font-semibold text-foreground-900">
                未找到符合条件的内容
              </h3>
              <p className="mt-2 max-w-md text-sm text-foreground-600">
                {query
                  ? `没有与“${query}”相关的资讯，换个关键词试试，或浏览下面的热门分类。`
                  : "输入关键词开始搜索，或从下面的热门分类开始浏览。"}
              </p>

              <div className="mt-7 w-full max-w-2xl">
                <p className="text-sm font-medium text-foreground-700">热门分类推荐</p>
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                  {allCategories.map((category) => (
                    <Link
                      key={category.id}
                      to={`/category/${category.id}`}
                      className="group flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300 cursor-pointer"
                    >
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                        <i className={`${category.icon} text-xl`}></i>
                      </span>
                      <span className="text-left">
                        <span className="block font-heading text-sm font-semibold text-foreground-950">
                          {category.name}
                        </span>
                        <span className="block text-xs text-foreground-500">
                          {category.subcategories.map((sub) => sub.name).join(" · ")}
                        </span>
                      </span>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="mt-7">
                <Link
                  to="/"
                  className="rounded-full bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  返回首页
                </Link>
              </div>
            </div>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}